import * as Yup from 'yup';

import { isInvoiceBillingType } from '~constants/maps';

import { getDaysMultiSelectOptions } from '~libs/dayjs';

import { TooltipIcon } from '~components';
import {
  faCircleExclamation,
  faCircleXmark,
  faInfoCircle,
  faSolidCircleCheck,
  faTriangleExclamation,
} from '~components/icon/iconLibrary';
import { Column, EditorContext, Row } from '~components/table-view';
import { multiSelectEditor, selectLazyEditor } from '~components/table-view/columnEditors';

import { Lookup, SahCategory, Shift } from '~lookups/models';
import { COPY_STATUS, CopyStatus, Section } from '~support-at-home/models';

import ExternalProvider from './ExternalProvider';

interface ServiceType {
  id: number;
  name: string;
  sah_service: { category: SahCategory };
}

const isInvoiceType = (row: Row) => {
  if (!row?.billing_type) return false;
  return isInvoiceBillingType(row?.billing_type);
};

const findMatchingService = (row: Row, context?: EditorContext, filteredServices?: any) => {
  const serviceTypeId = row?.service_type_id;
  const categoryId = context?.category_id;

  if (!serviceTypeId || !categoryId || !filteredServices) return null;

  const service = filteredServices.find((service: any) => service.id === serviceTypeId);
  if (!service) return null;

  if (service.sah_service?.category?.id !== categoryId) return null;

  return service;
};

const isMatchedService = (row: Row, context?: EditorContext, filteredServices?: any) => {
  if (!row?.service_type_id || !context?.category_id || !filteredServices) return true;
  return findMatchingService(row, context, filteredServices) !== null;
};

const isIgnoredService = (row: Row, context?: EditorContext, filteredServices?: any) => {
  const service = findMatchingService(row, context, filteredServices);
  return service?.sah_ignored === true;
};

const isRemovedCopyItem = (row: Row) => row?.copy_status === COPY_STATUS.REMOVED;

const renderCopyStatusIcon = (rowId: string, status: CopyStatus, previousAmount?: number) => {
  const formatAmount = (amount?: number) => (amount != null ? `$${Math.round(amount).toLocaleString()}` : '');

  switch (status) {
    case COPY_STATUS.CARRIED_OVER:
      return TooltipIcon({
        id: `item-copy-${rowId}`,
        icon: faSolidCircleCheck,
        iconColor: 'green',
        tooltip: 'Carried over from source budget.',
        placement: 'right',
      });
    case COPY_STATUS.PRICE_CHANGED:
      return TooltipIcon({
        id: `item-copy-${rowId}`,
        icon: faTriangleExclamation,
        iconColor: 'orange',
        tooltip: `Price changed from source budget (was ${formatAmount(previousAmount)}).`,
        placement: 'right',
      });
    case COPY_STATUS.REMOVED:
      return TooltipIcon({
        id: `item-copy-${rowId}`,
        icon: faCircleXmark,
        iconColor: 'red',
        tooltip: 'Service no longer available in the target price plan.',
        placement: 'right',
      });
    case COPY_STATUS.NEW:
      return TooltipIcon({
        id: `item-copy-${rowId}`,
        icon: faCircleExclamation,
        iconColor: 'dodgerblue',
        tooltip: 'New service available in the target price plan.',
        placement: 'right',
      });
    default:
      return false;
  }
};

export const columnsFactory = (
  shifts: Shift[],
  frequencies: Lookup[],
  filteredServices: any,
  _initialValues?: Section,
): Column[] => {
  const weekdayOptions = getDaysMultiSelectOptions().map(({ value, label }) => ({ id: value, name: label }));
  const frequencyOptions = frequencies.map(({ id, name }) => ({ id, name }));
  const shiftOptions = shifts.map(({ id, name }) => ({ id, name }));
  const serviceTypeOptions = filteredServices.map(({ id, name, sah_service: { category } }: ServiceType) => ({
    id,
    name,
    category_id: category?.id,
  }));

  // To be reimplemented for styling change to price / total on edit if needed
  // const initialItemValues = (rowId: string): SectionItem | undefined => {
  //   return initialValues?.items.find(({ id }) => id === rowId);
  // };

  const columns: Column[] = [
    {
      id: 'warning',
      header: '',
      type: 'readonly',
      style: { width: '40px' },
      classNames: {
        cell: 'cell-align-center text-warning',
      },
      renderReadOnly: (value, row, context) => {
        // Copy status indicators take priority
        const copyStatus = row?.copy_status as CopyStatus | undefined;
        if (copyStatus) {
          return renderCopyStatusIcon(String(row.id), copyStatus, row.previous_unit_amount);
        }

        if (!value) return false;

        // Check if service is broken (invalid/missing)
        const isBroken = !isMatchedService(row, context, filteredServices);

        if (isBroken) {
          return TooltipIcon({
            id: `item-warning-${row.id}`,
            icon: faTriangleExclamation,
            iconColor: 'red',
            tooltip: 'Service is no longer valid or has been filtered.',
            placement: 'right',
          });
        }

        return TooltipIcon({
          id: `item-info-${row.id}`,
          icon: faInfoCircle,
          iconColor: 'orange',
          tooltip: 'Service available because SAH filters are ignored.',
          placement: 'right',
        });
      },
      deriveValue: (row: Row, context?: EditorContext) => {
        if (row?.copy_status) return true;
        const isBroken = !isMatchedService(row, context, filteredServices);
        const isIgnored = isIgnoredService(row, context, filteredServices);
        return isBroken || isIgnored;
      },
    },
    {
      id: 'service_type_id',
      header: 'Service Type',
      type: 'select',
      options: serviceTypeOptions,
      style: { width: '300px' },
      classNames: {
        cell: 'cell-align-left',
      },
      onChange: onChangeServiceType,
      renderEditor: selectLazyEditor(
        (_row, context) => {
          return serviceTypeOptions.filter((option: any) => option.category_id === context.category_id);
        },
        (row) => (row.service_type ? { id: row.service_type.id, name: row.service_type.name } : null),
        false,
      ),
      isEditable: (row: Row, context?: EditorContext) =>
        !isRemovedCopyItem(row) && isMatchedService(row, context, filteredServices),
      validation: Yup.number().required('Service Type is required'),
    },
    {
      id: 'start_date',
      header: 'Start Date',
      type: 'date',
      style: { width: '150px' },
      classNames: {
        cell: 'cell-align-left',
      },
      isEditable: (row: Row, context?: EditorContext) =>
        !isRemovedCopyItem(row) && isMatchedService(row, context, filteredServices),
    },
    {
      id: 'end_date',
      header: 'End Date',
      type: 'date',
      style: { width: '150px' },
      classNames: {
        cell: 'cell-align-left',
      },
      isEditable: (row: Row, context?: EditorContext) =>
        !isRemovedCopyItem(row) && isMatchedService(row, context, filteredServices),
    },
    {
      id: 'shift_id',
      header: 'Shift',
      type: 'select',
      style: { width: '200px' },
      classNames: {
        cell: 'cell-align-left',
      },
      options: shiftOptions,
      onChange: async (_row, _context) => {
        return {
          week_days: [],
        };
      },
      isEditable: (row: Row, context?: EditorContext) =>
        !isRemovedCopyItem(row) && isMatchedService(row, context, filteredServices) && !isInvoiceType(row),
    },
    {
      id: 'week_days',
      header: 'Weekdays',
      type: 'multi-lookup',
      style: { width: '375px' },
      classNames: {
        cell: 'cell-align-left',
      },
      options: weekdayOptions,
      renderEditor: multiSelectEditor((row) => {
        if (!row.shift_id) return [];

        const shift = shifts.find(({ id }) => id === row.shift_id);
        const allow_weekdays = shift?.allow_weekdays ?? [];
        if (allow_weekdays.length === 0) return [];

        return weekdayOptions.filter(({ id }) => allow_weekdays.includes(Number(id)));
      }, true),
      isEditable: (row: Row, context?: EditorContext) =>
        !isRemovedCopyItem(row) && row.shift_id && isMatchedService(row, context, filteredServices) && !isInvoiceType(row),
    },
    {
      id: 'frequency_id',
      header: 'Frequency',
      type: 'select',
      style: { width: '185px' },
      classNames: {
        cell: 'cell-align-left',
      },
      options: frequencyOptions,
      isEditable: (row: Row, context?: EditorContext) =>
        !isRemovedCopyItem(row) && isMatchedService(row, context, filteredServices) && !isInvoiceType(row),
    },
    {
      id: 'quantity',
      header: 'Hrs/Qty',
      type: 'number',
      classNames: {
        header: 'cell-align-center',
        cell: 'cell-align-center',
      },
      style: { width: '50px' },
      isEditable: (row: Row, context?: EditorContext) =>
        !isRemovedCopyItem(row) && isMatchedService(row, context, filteredServices) && !isInvoiceType(row),
    },
    {
      id: 'external_provider',
      header: 'Ext. Provider Ref',
      type: 'custom',
      component: ExternalProvider,
      classNames: {
        header: 'cell-align-center',
        cell: 'cell-align-center',
      },
      style: { width: '200px' },
      deriveValue: ({ is_external_provider, external_reference, billing_type }) => {
        return {
          is_external_provider: is_external_provider ?? false,
          external_reference: external_reference ?? '',
          billing_type,
        };
      },
      onChange: onChangeExternalProvider,
      isEditable: (row: Row, context?: EditorContext) =>
        !isRemovedCopyItem(row) && isMatchedService(row, context, filteredServices),
    },
    {
      id: 'include_public_holidays',
      header: 'Pub Hol Incl.',
      type: 'checkbox',
      classNames: {
        header: 'cell-align-center',
        cell: 'cell-align-center',
      },
      style: { width: '100px' },
      isEditable: (row: Row, context?: EditorContext) =>
        !isRemovedCopyItem(row) && isMatchedService(row, context, filteredServices) && !isInvoiceType(row),
    },
    {
      id: 'unit_amount',
      header: 'Price',
      type: 'currency',
      classNames: {
        header: 'cell-align-right',
        cell: 'cell-align-right',
      },
      componentProps: {
        noDecimals: true,
      },
      style: { width: '200px' },
      isEditable: (row: Row, context?: EditorContext) =>
        !isRemovedCopyItem(row) && isMatchedService(row, context, filteredServices) && isInvoiceType(row),
    },
    {
      id: 'total_amount',
      header: 'Total',
      type: 'currency',
      classNames: {
        header: 'cell-align-right px-1',
        column: 'px-1',
        cell: 'cell-align-right',
      },
      componentProps: {
        noDecimals: true,
      },
      style: { width: '200px' },
      isEditable: false,
    },
  ];

  return columns;
};

const onChangeServiceType = async (_row: any, _context?: any) => {
  const columnsToReset = [
    'shift_id',
    'week_days',
    'frequency_id',
    'quantity',
    'external_provider',
    'include_public_holidays',
    'unit_amount',
    'total_amount',
    'billing_type',
  ];

  const resetValues = columnsToReset.reduce(
    (acc, key) => {
      if (key === 'week_days') acc[key] = [];
      else if (key === 'include_public_holidays') acc[key] = false;
      else if (key === 'external_provider') {
        acc.is_external_provider = false;
        acc.external_reference = '';
      } else acc[key] = null;
      return acc;
    },
    {} as Record<string, any>,
  );

  return resetValues;
};

const onChangeExternalProvider = async (row: Row, _context?: EditorContext, key?: string) => {
  if (!key) return {};
  const val = row[key as keyof Row];
  if (val == null) {
    return {
      is_external_provider: false,
      external_reference: '',
    };
  }

  const v = val as {
    is_external_provider?: boolean;
    external_reference?: string;
  };

  const isExternal = !!v.is_external_provider;

  return {
    is_external_provider: isExternal,
    external_reference: isExternal ? (v.external_reference ?? '') : '',
  };
};
